import React, { useState } from 'react';
import {
  Eye,
  MapPin,
  X,
  UserCheck,
  CreditCard,
  RefreshCw,
} from 'lucide-react';
import { PendingRegistration, AdminRole } from '../types';
import { Badge, Button, Search, Tooltip } from '../../components/core';

interface RegistrationApprovalsProps {
  registrations: PendingRegistration[];
  currentRole: AdminRole;
  onApprove?: (reg: PendingRegistration, assignedLevel: string, assignedBatch: string, notes?: string) => void;
  onReject?: (reg: PendingRegistration, reason: string) => void;
  addToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

export const RegistrationApprovals: React.FC<RegistrationApprovalsProps> = ({
  registrations,
  currentRole,
  addToast,
}) => {
  const [selectedReg, setSelectedReg] = useState<PendingRegistration | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  // Search & Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('ALL');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('ALL');
  const [regStatusFilter, setRegStatusFilter] = useState<string>('ALL');

  // Filtering Logic
  const filteredRegistrations = registrations.filter((reg) => {
    const query = searchQuery.trim().toLowerCase();
    const matchesSearch =
      !query ||
      reg.studentName.toLowerCase().includes(query) ||
      reg.parentMobile.includes(query) ||
      (reg.paymentTxnId && reg.paymentTxnId.toLowerCase().includes(query)) ||
      reg.id.toLowerCase().includes(query) ||
      reg.parentName.toLowerCase().includes(query);

    const matchesPaymentStatus =
      paymentStatusFilter === 'ALL' || (reg.paymentStatus || 'Paid') === paymentStatusFilter;

    const matchesPaymentMethod =
      paymentMethodFilter === 'ALL' || (reg.paymentMethod || 'UPI') === paymentMethodFilter;

    const matchesRegStatus =
      regStatusFilter === 'ALL' || reg.status === regStatusFilter;

    return matchesSearch && matchesPaymentStatus && matchesPaymentMethod && matchesRegStatus;
  });

  const totalCount = registrations.length;
  const paidCount = registrations.filter((r) => (r.paymentStatus || 'Paid') === 'Paid').length;

  const resetFilters = () => {
    setSearchQuery('');
    setPaymentStatusFilter('ALL');
    setPaymentMethodFilter('ALL');
    setRegStatusFilter('ALL');
  };

  const getPaymentStatusVariant = (status: string): 'success' | 'warning' | 'danger' => {
    if (status === 'Paid') return 'success';
    if (status === 'Pending') return 'warning';
    return 'danger';
  };

  const getRegStatusVariant = (status: string, payStatus: string): 'success' | 'warning' | 'danger' => {
    if (status === 'Approved' || payStatus === 'Paid') return 'success';
    if (status === 'Pending') return 'warning';
    return 'danger';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="admin-card flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--gv-ink)]">Student Registration Management</h1>
        </div>
      </div>

      {/* Filter and Search Controls */}
      <div className="admin-card space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Search
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search Student Name, Mobile, or Txn ID..."
          />

          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="ALL">All Payment Statuses</option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
          </select>

          <select
            value={paymentMethodFilter}
            onChange={(e) => setPaymentMethodFilter(e.target.value)}
            className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="ALL">All Payment Methods</option>
            <option value="UPI">UPI</option>
            <option value="QR Code">QR Code</option>
            <option value="Card">Card</option>
            <option value="Net Banking">Net Banking</option>
          </select>

          <select
            value={regStatusFilter}
            onChange={(e) => setRegStatusFilter(e.target.value)}
            className="admin-input h-9 w-full rounded-xl border border-[var(--gv-border)] bg-white px-3 py-2 text-xs font-medium text-[var(--gv-ink)] focus-visible:border-[var(--gv-brand)] focus-visible:outline-none"
          >
            <option value="ALL">All Registration Statuses</option>
            <option value="Approved">Approved / Registered</option>
            <option value="Pending">Pending</option>
            <option value="Rejected">Rejected</option>
          </select>
        </div>

        {(searchQuery || paymentStatusFilter !== 'ALL' || paymentMethodFilter !== 'ALL' || regStatusFilter !== 'ALL') && (
          <div className="flex items-center justify-between border-t border-[var(--gv-border)] pt-2 text-xs">
            <span className="text-[var(--gv-ink-muted)]">
              Showing <strong className="text-[var(--gv-ink)]">{filteredRegistrations.length}</strong> of {registrations.length} records
            </span>
            <Button variant="ghost" size="sm" leftIcon={RefreshCw} onClick={resetFilters}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {/* Registration Table */}
      <div className="admin-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-[var(--gv-border)] p-4">
          <h3 className="flex items-center gap-2 text-sm font-bold text-[var(--gv-ink)]">
            <UserCheck className="h-4 w-4 text-[var(--gv-brand)]" />
            <span>Student Registrations List ({filteredRegistrations.length})</span>
          </h3>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs text-[var(--gv-ink-muted)]">
            <thead className="border-b border-[var(--gv-border)] bg-stone-50 font-mono text-[10px] uppercase tracking-wider text-[var(--gv-ink-muted)]">
              <tr>
                <th className="px-4 py-2.5 font-bold">Reg ID</th>
                <th className="px-4 py-2.5 font-bold">Applicant & Parent</th>
                <th className="px-4 py-2.5 font-bold">Parent Mobile</th>
                <th className="px-4 py-2.5 font-bold">Level & Batch</th>
                <th className="px-4 py-2.5 font-bold">Fee</th>
                <th className="px-4 py-2.5 font-bold">Payment Status</th>
                <th className="px-4 py-2.5 font-bold">Payment Method</th>
                <th className="px-4 py-2.5 font-bold">Txn ID</th>
                <th className="px-4 py-2.5 font-bold">Payment Date & Time</th>
                <th className="px-4 py-2.5 font-bold">Status</th>
                <th className="px-4 py-2.5 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white font-medium">
              {filteredRegistrations.map((reg) => {
                const feeAmount = reg.registrationFee ?? 100;
                const payStatus = reg.paymentStatus ?? 'Paid';
                const payMethod = reg.paymentMethod ?? 'UPI';
                const txnId = reg.paymentTxnId ?? 'TXN_DEMO';
                const payDate = reg.paymentDate ?? reg.submissionDate;

                return (
                  <tr key={reg.id} className="transition-colors hover:bg-stone-50/80">
                    <td className="px-4 py-3 font-mono font-bold text-[var(--gv-brand)]">{reg.id}</td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-[var(--gv-ink)]">{reg.studentName}</div>
                      <div className="text-[10px] text-[var(--gv-ink-muted)]">Parent: {reg.parentName}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-[var(--gv-ink)]">{reg.parentMobile}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[var(--gv-ink)]">{reg.requestedLevel}</div>
                      <div className="text-[10px] text-[var(--gv-ink-muted)]">{reg.preferredBatch}</div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono font-bold text-[var(--gv-ink)]">
                      ₹{feeAmount}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <Badge variant={getPaymentStatusVariant(payStatus)}>
                        {payStatus}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-medium text-[var(--gv-ink-muted)]">
                      {payMethod}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-[11px] text-[var(--gv-ink-muted)]">
                      {txnId}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 font-mono text-[11px] text-[var(--gv-ink-muted)]">
                      {payDate}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <Badge variant={getRegStatusVariant(reg.status, payStatus)}>
                        {reg.status === 'Approved' ? 'Registered' : reg.status}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <Tooltip content="View Registration Details">
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={Eye}
                          aria-label={`View registration ${reg.id}`}
                          onClick={() => {
                            setSelectedReg(reg);
                            setIsPreviewModalOpen(true);
                          }}
                        >
                          View
                        </Button>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })}
              {filteredRegistrations.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-8 text-center font-medium text-[var(--gv-ink-muted)]">
                    No student registrations found matching the applied filters or search query.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Registration Details Page / Modal */}
      {isPreviewModalOpen && selectedReg && (
        <div className="fixed inset-0 z-[9990] flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-xs">
          <div className="admin-card w-full max-w-lg space-y-4 p-6 text-[var(--gv-ink)] shadow-xl">
            <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-3">
              <div>
                <span className="font-mono text-[10px] font-bold uppercase text-[var(--gv-brand)]">Registration Record</span>
                <h3 className="text-base font-bold text-[var(--gv-ink)]">Application Details: {selectedReg.id}</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                leftIcon={X}
                aria-label="Close"
                onClick={() => setIsPreviewModalOpen(false)}
              />
            </div>

            <div className="space-y-3.5 text-xs">
              {/* Student Basic Details */}
              <div className="rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3.5">
                <span className="font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Student Name</span>
                <p className="text-sm font-bold text-[var(--gv-ink)]">{selectedReg.studentName}</p>
                <div className="mt-1 flex items-center gap-3 text-[11px] text-[var(--gv-ink-muted)]">
                  <span>Requested Level: <strong className="text-[var(--gv-ink)]">{selectedReg.requestedLevel}</strong></span>
                  <span>Batch: <strong className="text-[var(--gv-ink)]">{selectedReg.preferredBatch}</strong></span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                  <span className="font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Parent Name</span>
                  <p className="font-bold text-[var(--gv-ink)]">{selectedReg.parentName}</p>
                </div>
                <div className="rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3">
                  <span className="font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Parent Mobile</span>
                  <p className="font-mono font-bold text-[var(--gv-brand)]">{selectedReg.parentMobile}</p>
                </div>
              </div>

              {/* READ-ONLY PAYMENT INFORMATION SECTION */}
              <div className="space-y-3 rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-4">
                <div className="flex items-center justify-between border-b border-[var(--gv-border)] pb-2">
                  <div className="flex items-center gap-1.5 text-xs font-bold text-[var(--gv-ink)]">
                    <CreditCard className="h-4 w-4 text-[var(--gv-brand)]" />
                    <span>Payment Information</span>
                  </div>
                  <Badge variant={getPaymentStatusVariant(selectedReg.paymentStatus || 'Paid')}>
                    {selectedReg.paymentStatus || 'Paid'}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="block font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Registration Fee</span>
                    <p className="font-mono text-sm font-bold text-[var(--gv-ink)]">
                      ₹{(selectedReg.registrationFee ?? 100).toFixed(2)}
                    </p>
                  </div>

                  <div>
                    <span className="block font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Payment Method</span>
                    <p className="font-semibold text-[var(--gv-ink)]">
                      {selectedReg.paymentMethod || 'UPI'}
                    </p>
                  </div>

                  <div>
                    <span className="block font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Transaction ID</span>
                    <p className="font-mono font-bold text-[var(--gv-brand)]">
                      {selectedReg.paymentTxnId || 'TXN_98234120'}
                    </p>
                  </div>

                  <div>
                    <span className="block font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Payment Date & Time</span>
                    <p className="font-mono text-[11px] font-medium text-[var(--gv-ink-muted)]">
                      {selectedReg.paymentDate || selectedReg.submissionDate}
                    </p>
                  </div>
                </div>
              </div>

              {/* Address Verification */}
              <div className="rounded-xl border border-[var(--gv-border)] bg-[var(--gv-surface)] p-3.5">
                <span className="font-mono text-[10px] uppercase text-[var(--gv-ink-muted)]">Residential Address</span>
                <p className="mt-0.5 font-medium text-[var(--gv-ink)]">{selectedReg.address}</p>
                <div className="mt-2 flex items-center justify-between border-t border-[var(--gv-border)] pt-2 font-mono text-[10px]">
                  <div className="flex items-center gap-1.5 text-emerald-700">
                    <MapPin className="h-3.5 w-3.5" />
                    <span>Location Verified</span>
                  </div>
                  <span className="text-[var(--gv-ink-muted)]">Submitted: {selectedReg.submissionDate}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" size="sm" onClick={() => setIsPreviewModalOpen(false)}>
                Close Details
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
